import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import type { CreateBatchAdsMutation, CreateBatchAdsMutationVariables } from '../../../../../../../apis/graphql';
import { useTraffickingTableReload } from '../../../../../../../contexts/TraffickingTableReloadContext';
import type { DuplicateLineItem } from '../../../DuplicateAdsStepperProvider/types';
import { CREATE_ADS_BATCH } from '../mutations/batchCreateAds';
import type { DuplicateAdFormValue } from '../types';
import { getCreateBatchAdsInputs, getDuplicateBatchSaveContexts, onSavingAdsError, onSavingAdsSuccess } from './utils';
import { writeDuplicateBatchAdsToCache } from './writeDuplicateBatchAdsToCache';

type UseSaveAdsResult = {
  saveAds: (
    selectedLineItems: DuplicateLineItem[],
    duplicateAdFromValues: DuplicateAdFormValue[],
    onCloseCallback: () => void
  ) => Promise<void>;
  loading: boolean;
};

export const useSaveAds = (): UseSaveAdsResult => {
  const [batchCreateAds, { loading }] = useMutation<CreateBatchAdsMutation, CreateBatchAdsMutationVariables>(
    CREATE_ADS_BATCH
  );
  const { scheduleTableReload } = useTraffickingTableReload();

  const saveAds = useCallback(
    async (
      lineItems: DuplicateLineItem[],
      duplicateAdFromValues: DuplicateAdFormValue[],
      onCloseCallback: () => void
    ): Promise<void> => {
      const adsInput = getCreateBatchAdsInputs(lineItems, duplicateAdFromValues);
      const duplicateContexts = getDuplicateBatchSaveContexts(lineItems, duplicateAdFromValues);

      await batchCreateAds({
        variables: { adsInput },
        update(cache, result) {
          if (
            result.data?.createAdsBatchV5?.length &&
            duplicateContexts.length === result.data.createAdsBatchV5.length
          ) {
            writeDuplicateBatchAdsToCache(cache, result.data, duplicateContexts);
          }
        },
      }).then(({ data, errors }) => {
        if (errors?.length) {
          onSavingAdsError(adsInput, errors);
        } else {
          if (data?.createAdsBatchV5?.length) onSavingAdsSuccess(data.createAdsBatchV5.length);

          // Newly duplicated ads can't be placed into the searcher's sort order/page locally;
          // schedule a delayed refetch so they land in the correct position.
          scheduleTableReload();

          onCloseCallback();
        }
      });
    },
    [batchCreateAds, scheduleTableReload]
  );

  return {
    loading,
    saveAds,
  };
};
